import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  effect,
  input,
  output,
  ViewChildren,
  QueryList,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  firstValueFrom,
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { noteService, NoteAttachment } from 'src/app/services/note-service';
import { HtmlEditorComponent } from 'src/app/shared/html-editor/html-editor.component';
import { ActivatedRoute } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/dialog/confirm.dialog';
import { CommunicationService } from '../../signal-lead.serice';
import { TruncatePipe } from 'src/app/pipe/truncate.pipe';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { ItemComponent } from 'src/app/shared/item/item.component';
import { NoteDraftService } from './note.draft.service';
import { userDataService } from 'src/app/services/userDataService';
import { SocketService } from 'src/app/services/socket.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-notes',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    HtmlEditorComponent,
    FormsModule,
    ReactiveFormsModule,
    TablerIconsModule,
    ItemComponent,
  ],
  templateUrl: './notes.component.html',
  styleUrl: './notes.component.scss',
})
export class NotesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('contentElement') contentElements!: QueryList<ElementRef>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  id: any;
  noteOpen: boolean = true;
  notesContent = new FormControl();
  data = input<any>();
  lead = input<any>();
  params = input<any>();
  isEditing: any = null;
  paramsChild: any;
  currentLead: any;
  total: any;
  showLoad: boolean = true;
  receivedData = this.CommunicationService.data;
  isModal = input<boolean>(false);
  userRole: 'USER' | 'ADMIN' = 'USER';

  private currentUserId: number | null = null;
  notes: any[];

  userNoteUsage: any[] = [];
  loadingSuggestions: boolean = false;
  isSaving: boolean = false;

  // Attachments
  pendingFiles: File[] = []; // Files waiting to be uploaded on save
  existingAttachments: NoteAttachment[] = []; // Already saved attachments (for edit mode)
  isUploading: boolean = false;
  maxFileSize = 10 * 1024 * 1024; // 10MB
  allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ];

  private destroy$ = new Subject<void>();
  private userId: string = '';
  hasDraft: boolean = false;
  draftLoaded: boolean = false;
  lastDraftSave: Date | null = null;
  private isViewInitialized: boolean = false;
  private pendingEditorExpansion: boolean = false;

  quillModule = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
        ['speechToText'],
        ['attachment'],
      ],
      handlers: {
        speechToText: () => {},
        attachment: () => this.triggerFileInput(),
      },
    },
  };

  constructor(
    private noteService: noteService,
    private dialog: MatDialog,
    private CommunicationService: CommunicationService,
    private noteDraftService: NoteDraftService,
    private userDataService: userDataService,
    private socketService: SocketService,
    private snackBar: MatSnackBar,
  ) {
    effect(() => {
      const newData = this.receivedData();
      this.onDataReceived(newData);
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      const userData = await this.userDataService.getUserData();
      this.userRole = userData?.role || 'USER';

      if (userData) {
        this.userId =
          userData.id ||
          userData._id ||
          userData.userId ||
          userData.user_id ||
          '';
        this.currentUserId = userData.id || null;

        if (!this.userId) {
          console.warn('User data exists but no ID found.');
        }
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }

    this.paramsChild = this.params();
    this.notes = this.data().data;
    this.total = this.data().total;
    this.currentLead = this.lead();
    this.id = this.currentLead?.id;

    if (+this.paramsChild.pageIndex * +this.paramsChild.pageSize >= this.total) {
      this.showLoad = false;
    }

    this.loadUserNoteUsage();
    this.setupNoteUpdateListener();
    this.registerAttachmentIcon();
  }

  private registerAttachmentIcon(): void {
    const Quill = (window as any).Quill;
    if (Quill) {
      const icons = Quill.import('ui/icons');
      icons.attachment = `<svg viewBox="0 0 24 24" width="18" height="18">
        <path fill="currentColor" d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
      </svg>`;
    }
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    const validFiles: File[] = [];

    for (const file of files) {
      if (file.size > this.maxFileSize) {
        this.snackBar.open(
          `File "${file.name}" exceeds 10MB limit`,
          'Close',
          { duration: 3000 },
        );
        continue;
      }
      if (!this.allowedFileTypes.includes(file.type)) {
        this.snackBar.open(
          `File type "${file.type}" not allowed`,
          'Close',
          { duration: 3000 },
        );
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      input.value = '';
      return;
    }

    const totalCount = this.pendingFiles.length + this.existingAttachments.length;
    if (totalCount + validFiles.length > 5) {
      this.snackBar.open(
        'Maximum 5 attachments allowed per note',
        'Close',
        { duration: 3000 },
      );
      input.value = '';
      return;
    }

    // Store files locally - upload happens on save
    this.pendingFiles.push(...validFiles);
    this.snackBar.open(
      `${validFiles.length} file(s) added`,
      'Close',
      { duration: 2000 },
    );

    input.value = '';
  }

  removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
  }

  removeExistingAttachment(index: number): void {
    this.existingAttachments.splice(index, 1);
  }

  getFileIcon(type?: string): string {
    if (!type) return 'insert_drive_file';
    if (type.startsWith('image/')) return 'image';
    if (type === 'application/pdf') return 'picture_as_pdf';
    if (type.includes('word')) return 'description';
    if (type.includes('excel') || type.includes('spreadsheet'))
      return 'table_chart';
    return 'insert_drive_file';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if (this.userId) {
      this.initializeDraftingSystem();
    } else {
      console.warn('Cannot initialize drafting system - no user ID available');
      this.retryUserDataAndInitializeDrafts();
    }
  }

  private async retryUserDataAndInitializeDrafts() {
    try {
      const userData = await this.userDataService.getUserData();
      if (userData) {
        this.userId =
          userData.id ||
          userData._id ||
          userData.userId ||
          userData.user_id ||
          '';
        if (this.userId) {
          this.initializeDraftingSystem();
        }
      }
    } catch (error) {
      console.error('Retry failed to get user data:', error);
    }
  }

  private async initializeDraftingSystem() {
    if (!this.userId) {
      console.warn('Cannot initialize drafting system without user ID');
      return;
    }
    this.setupAutoSave();
    await this.loadDraft();
  }

  async loadDraft() {
    if (!this.id || !this.userId || !this.isViewInitialized) return;

    try {
      const draft = await this.noteDraftService.getDraft(this.id, this.userId);

      if (draft && draft.content) {
        this.notesContent.setValue(draft.content);
        this.hasDraft = true;
        this.lastDraftSave = new Date(draft.timestamp);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }

    this.draftLoaded = true;
  }

  setupAutoSave() {
    if (!this.isViewInitialized) return;

    this.notesContent.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged())
      .subscribe(async (content) => {
        if (!this.isEditing && this.noteOpen && this.isViewInitialized) {
          if (!content || !content.trim()) {
            await this.clearDraft();
          } else {
            await this.saveDraft(content);
          }
        }
      });
  }

  async saveDraft(content: string) {
    if (!this.id || !this.userId || !this.isViewInitialized) return;

    if (!content || !content.trim()) {
      await this.clearDraft();
      return;
    }

    try {
      await this.noteDraftService.saveDraft(this.id, this.userId, content);
      this.hasDraft = true;
      this.lastDraftSave = new Date();
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  }

  async clearDraft() {
    if (!this.id || !this.userId || !this.isViewInitialized) return;

    try {
      await this.noteDraftService.deleteDraft(this.id, this.userId);
      this.hasDraft = false;
      this.lastDraftSave = null;
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }

  expandEditor() {
    if (!this.isEditing) {
      this.noteOpen = true;
      this.loadUserNoteUsage();

      if (this.isViewInitialized && this.draftLoaded) {
        this.loadDraftForEditing();
      }
    }
  }

  async loadDraftForEditing() {
    if (!this.id || !this.userId || !this.isViewInitialized) return;

    try {
      const draft = await this.noteDraftService.getDraft(this.id, this.userId);

      if (draft && draft.content && !this.notesContent.value) {
        this.notesContent.setValue(draft.content);
        this.hasDraft = true;
        this.lastDraftSave = new Date(draft.timestamp);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
  }

  async loadUserNoteUsage() {
    this.loadingSuggestions = true;
    try {
      const response = await firstValueFrom(
        this.noteService.get({}, 'usage'),
      );
      if (response) {
        this.userNoteUsage = response;
      }
    } catch (error) {
      console.error('Failed to load note suggestions:', error);
      this.userNoteUsage = [];
    } finally {
      this.loadingSuggestions = false;
    }
  }

  insertSuggestion(suggestion: any) {
    if (this.notesContent) {
      const currentContent = this.notesContent.value || '';
      const newContent = currentContent
        ? currentContent + ' ' + suggestion.text
        : suggestion.text;
      this.notesContent.setValue(newContent);
    }
  }

  async cancel() {
    if (this.noteOpen && !this.isEditing) {
      await this.clearDraft();
      this.notesContent.reset();
    }

    this.pendingFiles = [];
    this.existingAttachments = [];
    this.isEditing = false;
    this.noteOpen = false;
    this.isSaving = false;
  }

  onDataReceived(event: any) {
    if (event.type == 'focusNoteComponent') {
      if (this.notes && Array.isArray(this.notes)) {
        const updatedObj = this.notes.find((x) => x.id == event.obj.data.id);
        if (updatedObj) {
          updatedObj.pined = event.pined;
        }
      }
    }
  
    if (event.type == 'note-added-external') {
      const note = event.obj?.data;
      if (note && note.lead_id === this.id && !this.notes?.some(n => n.id === note.id)) {
        this.insertNoteWithSort(note);
        this.total++;
      }
    }
  }

  async togglePin(event: any) {
    const updatedObj = this.notes.find((x) => x.id == event.id);
    const lastPin = updatedObj.pined;
    updatedObj.pined = !lastPin;

    const response = await firstValueFrom(
      this.noteService.patch(updatedObj, updatedObj.id),
    );

    if (response) {
      const obj = { data: response, location: 'note', update: false };
      this.CommunicationService.updateData({
        type: 'focus',
        obj,
      });
    }
  }

  async deleteRow(event: any) {
    const header = 'Delete note';
    const content = 'Do you confirm deleting this note?';
    const confirmed = await this.callDialog(header, content);
    if (confirmed) {
      const response = await firstValueFrom(this.noteService.delete(event.id));
      if (response) {
        const index = this.notes.findIndex((note) => note.id === event.id);
        this.total--;
        this.notes.splice(index, 1);
        if (event.pined) {
          const obj = { data: event, location: 'note', update: false };
          this.CommunicationService.updateData({
            type: 'focus',
            obj,
          });
        }
      }
    }
  }

// WITH this:
editRow(event: any) {
  if (event) {
    this.isEditing = event;
    // Clear parent's pending files when editing starts in item
    this.pendingFiles = [];
    this.existingAttachments = event?.attachments ? [...event.attachments] : [];

    if (event.content && this.isViewInitialized) {
      this.saveDraft(event.content);
    }
  } else {
    // event is false when canceling edit
    this.isEditing = null;
    this.pendingFiles = [];
    this.existingAttachments = [];
  }
}

  async moreData() {
    this.paramsChild.pageIndex++;
    if (
      +this.paramsChild.pageIndex * +this.paramsChild.pageSize >=
      this.total
    ) {
      this.showLoad = false;
    }
    const response = await firstValueFrom(
      this.noteService.get(this.paramsChild),
    );
    this.notes.push(...response.data);
  }

  callDialog(header: string, content: string) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header,
        content,
      },
      width: '300px',
    });

    return dialogRef.afterClosed().toPromise();
  }

  async save(event?: any) {
    if (this.isSaving) {
      return;
    }

    if (this.notesContent.value || event) {
      this.isSaving = true;
      this.isUploading = true;

      try {
        let response: any;

        if (event) {
          // Editing existing note via item component
          const data: any = {
            content: event.content,
          };

          if (event.removeAttachmentIds?.length) {
            data.removeAttachmentIds = event.removeAttachmentIds;
          }

          // Get new files from event (if item component supports adding files)
          const newFiles = event.newFiles || [];

          response = await firstValueFrom(
            this.noteService.updateWithFiles(event.id, data, newFiles),
          );
        } else {
          // Creating new note
          const data = {
            content: this.notesContent.value,
            lead_id: this.id,
          };

          response = await firstValueFrom(
            this.noteService.createWithFiles(data, this.pendingFiles),
          );
        }

        if (response) {
          if (event) {
            // Update existing note in list
            const index = this.notes.findIndex((note) => note.id === event.id);
            if (index !== -1) this.notes[index] = response;
            if (response.pined) {
              const obj = { data: response, location: 'note', update: true };
              this.CommunicationService.updateData({
                type: 'focus',
                obj,
              });
            }
            await this.clearDraft();
          } else {
            // Add new note to list
            const obj = {
              data: response,
              location: 'add-note',
              update: true,
              newNote: true,
            };
            this.CommunicationService.updateData({
              type: 'add-note',
              obj,
            });

            this.insertNoteWithSort(response.data);
            this.total++;
            await this.clearDraft();
          }
        }

        this.notesContent.reset();
        this.pendingFiles = [];
        this.existingAttachments = [];
        this.cancel();
      } catch (error) {
        console.error('Error saving note:', error);
        this.snackBar.open('Failed to save note', 'Close', { duration: 3000 });
      } finally {
        this.isSaving = false;
        this.isUploading = false;
      }
    }
  }

  async removeSuggestion(event: Event, suggestion: any) {
    event.stopPropagation();

    const confirmed = await this.callDialog(
      'Remove Suggestion',
      'Are you sure you want to remove this suggestion?',
    );

    if (!confirmed) return;

    try {
      await firstValueFrom(this.noteService.delete(suggestion.id, 'usage'));
      this.userNoteUsage = this.userNoteUsage.filter(
        (s) => s.id !== suggestion.id,
      );
    } catch (error) {
      console.error('Failed to remove suggestion:', error);
    }
  }

  ngOnDestroy(): void {
    const currentContent = this.notesContent.value;
    if (
      currentContent &&
      currentContent.trim() &&
      this.noteOpen &&
      !this.isEditing &&
      this.isViewInitialized
    ) {
      this.saveDraft(currentContent);
    }

    const objDestroy = {
      data: this.notes,
      params: this.paramsChild,
      total: this.total,
      update: false,
    };
    this.CommunicationService.updateData({
      type: 'destroy',
      obj: objDestroy,
    });

    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupNoteUpdateListener(): void {
    this.socketService
      .getLeadUpdates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        if (update.leadId !== this.id) {
          return;
        }

        switch (update.type) {
          case 'note_added':
            this.handleNoteAdded(update.data, update.userId);
            break;
          case 'note_updated':
            this.handleNoteUpdated(update.data, update.userId);
            break;
          case 'note_deleted':
            this.handleNoteDeleted(update.data.id, update.userId);
            break;
        }
      });
  }

  private handleNoteAdded(note: any, userId: number): void {
    if (userId === this.currentUserId) {
      return;
    }

    if (this.notes?.some((n) => n.id === note.id)) {
      return;
    }

    this.insertNoteWithSort(note);
    this.total++;
  }

  private handleNoteUpdated(note: any, userId: number): void {
    const index = this.notes?.findIndex((n) => n.id === note.id);
    if (index !== -1) {
      const oldNote = this.notes[index];

      if (oldNote.pined !== note.pined) {
        this.notes.splice(index, 1);

        const firstNonPinnedIndex = this.notes.findIndex((n) => !n.pined);
        if (firstNonPinnedIndex === -1) {
          this.notes.push(note);
        } else {
          this.notes.splice(firstNonPinnedIndex, 0, note);
        }
      } else {
        this.notes[index] = note;
      }

      this.notes = [...this.notes];
    }
  }

  private handleNoteDeleted(noteId: number, userId: number): void {
    if (userId === this.currentUserId) {
      return;
    }

    const index = this.notes?.findIndex((n) => n.id === noteId);
    if (index !== -1) {
      this.notes.splice(index, 1);
      this.notes = [...this.notes];
      this.total = Math.max(0, this.total - 1);
    }
  }

  private insertNoteWithSort(note: any): void {
    this.notes.push(note);
    this.notes.sort((a, b) => {
      const aPinned = a.pined === true;
      const bPinned = b.pined === true;

      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
    this.notes = [...this.notes];
  }
}